import React from 'react';
import { fireEvent, render, screen } from '@testing-library/react';
import userEvent from '@testing-library/user-event';

import ConfigProvider from 'components/ConfigProvider';

import { toFormValues } from '../fields';
import { OPTIONS } from '../fixtures';
import UserDetailsForm from './UserDetailsForm';

const VALUES = toFormValues({
  firstName: 'Abe',
  lastName: 'Martinez',
  email: 'martinez@email.com',
  mobile: '(350) 579-3153',
  mobileCountryCode: 1,
  countryCode: 'US',
  state: 'TX',
  city: 'Dallas',
  postalCode: '75001',
});

// PD-4684. As Management::UserDetail sends it: the offer, guaranteed to contain
// whatever the user signs in with today.
const AUTHENTICATION = {
  assignment: null,
  locked: false,
  provider: null,
  requireSso: false,
  options: [
    { value: 'paccar', label: 'PCAR (Pass)' },
    { value: 'password', label: 'Password' },
  ],
};

const renderForm = (props = {}) =>
  render(
    <ConfigProvider>
      <UserDetailsForm
        authentication={AUTHENTICATION}
        onChange={jest.fn()}
        options={OPTIONS}
        username="m.abe"
        values={VALUES}
        {...props}
      />
    </ConfigProvider>,
  );

describe('management/EditUser UserDetailsForm', () => {
  it('groups the fields under the design’s two headings', () => {
    renderForm();

    expect(
      screen.getByRole('heading', { name: 'User Information' }),
    ).toBeInTheDocument();
    expect(
      screen.getByRole('heading', { name: 'Additional Information' }),
    ).toBeInTheDocument();
  });

  it('shows the current values', () => {
    renderForm();

    expect(screen.getByLabelText(/First Name/)).toHaveValue('Abe');
    expect(screen.getByLabelText(/Email/)).toHaveValue('martinez@email.com');
    expect(screen.getByLabelText(/City/)).toHaveValue('Dallas');
  });

  it('reports edits by field name', () => {
    const onChange = jest.fn();
    renderForm({ onChange });

    userEvent.type(screen.getByLabelText(/First Name/), 'x');

    expect(onChange).toHaveBeenCalledWith('firstName', 'Abex');
  });

  // The ticket is explicit: visible, non-editable, shown as a disabled field rather
  // than hidden.
  it('shows the username disabled rather than hiding it', () => {
    renderForm();

    const username = screen.getByLabelText(/Username/);

    expect(username).toHaveValue('m.abe');
    expect(username).toBeDisabled();
  });

  it('marks only the three genuinely required fields', () => {
    renderForm();

    expect(screen.getByLabelText(/First Name/)).toBeRequired();
    expect(screen.getByLabelText(/Last Name/)).toBeRequired();
    expect(screen.getByLabelText(/Email/)).toBeRequired();
    expect(screen.getByLabelText(/City/)).not.toBeRequired();
  });

  it('carries the help text the design specifies', () => {
    renderForm();

    expect(
      screen.getByText(
        'This Job Role will appear by your contact name for additional context.',
      ),
    ).toBeInTheDocument();
    expect(
      screen.getByText('Required for SMS notifications.'),
    ).toBeInTheDocument();
  });

  it('disables every field for a viewer who may not edit', () => {
    renderForm({ disabled: true });

    expect(screen.getByLabelText(/First Name/)).toBeDisabled();
    expect(screen.getByLabelText(/City/)).toBeDisabled();
  });

  it('shows a server-side message against the field it belongs to', () => {
    renderForm({ errors: { email: ['has already been taken'] } });

    expect(screen.getByText('has already been taken')).toBeInTheDocument();
  });

  // One in front of each number, all three live since PD-4068 added
  // phone_country_code and fax_country_code (mobile_country_code was already there).
  describe('the country codes', () => {
    // All three share the label, so they are told apart by position. DOM order
    // follows the design: mobile closes the first column, phone and fax open the
    // second.
    const countryCodes = () => screen.getAllByLabelText('Country Code');

    // Only one is opened per example: two open popovers put two '+52' options in the
    // document and the query would match both.
    const openedCode = (index) => {
      renderForm({ onChange });
      userEvent.click(countryCodes()[index]);

      return screen.getByText('+52');
    };

    let onChange;

    beforeEach(() => {
      onChange = jest.fn();
    });

    it('offers one for each of the three numbers', () => {
      renderForm();

      expect(countryCodes()).toHaveLength(3);
    });

    // Each reports under the field name of the number it belongs to, which is the
    // camelCase name UserDetail sends and toParams maps back to the column.
    it.each([
      [0, 'mobileCountryCode'],
      [1, 'phoneCountryCode'],
      [2, 'faxCountryCode'],
    ])('records a choice made in the one at %i', (index, field) => {
      // fireEvent, not userEvent: the popover option carries pointer-events: none, so
      // userEvent refuses to click it. Same as PermissionsCell's tests.
      fireEvent.click(openedCode(index));

      expect(onChange).toHaveBeenCalledWith(field, '52');
    });

    // Read from the trigger's text — a KeyDS Select renders its label as the text of a
    // button, so toHaveValue is empty whatever the field shows.
    it('shows the code on file for each number', () => {
      renderForm({
        values: {
          ...VALUES,
          phoneCountryCode: '52',
          faxCountryCode: '1',
        },
      });

      expect(countryCodes()[0]).toHaveTextContent('+1');
      expect(countryCodes()[1]).toHaveTextContent('+52');
      expect(countryCodes()[2]).toHaveTextContent('+1');
    });
  });

  // jsdom computes no layout, so these pin the two declarations rather than the
  // spacing itself. Both are load-bearing: KeyDS puts `flex: 1` on the container it
  // wraps every field in, so a stretched column hands its spare height to whichever
  // fields are bare ones and opens gaps that look like a field is missing. See
  // styles.js.
  describe('the two columns', () => {
    // Found from the heading a column opens with rather than by walking down from the
    // render container, which starts with a wrapper of ConfigProvider's.
    const column = () =>
      screen.getByRole('heading', { name: 'User Information' }).parentElement;

    it('leaves each column as tall as its own fields', () => {
      renderForm();

      expect(column().parentElement).toHaveStyleRule(
        'align-items',
        'flex-start',
      );
    });

    it('holds each field to its natural height', () => {
      renderForm();

      expect(column()).toHaveStyleRule('flex', '0 0 auto', { modifier: '> *' });
    });
  });

  describe('the state options', () => {
    // Exactly 'State', because /State/ also matches the Country select showing
    // "United States".
    const stateSelect = () => screen.getByLabelText('State');

    // Alabama rather than Texas: Texas is already the field's value, so it appears in
    // the closed trigger too and would match whether the list opened or not.
    it('offers the chosen country’s subdivisions', () => {
      renderForm();

      userEvent.click(stateSelect());

      expect(screen.getByText('Alabama')).toBeInTheDocument();
    });

    it('narrows to the chosen country', () => {
      renderForm({ values: { ...VALUES, countryCode: 'PR', state: '' } });

      userEvent.click(stateSelect());

      expect(screen.queryByText('Alabama')).not.toBeInTheDocument();
    });

    // Puerto Rico genuinely has none on file, which the legacy form also renders as
    // an empty select rather than an error.
    it('copes with a country that has none', () => {
      renderForm({ values: { ...VALUES, countryCode: 'PR', state: '' } });

      expect(stateSelect()).toBeInTheDocument();
    });

    // A country outside the states map must not blow the page up.
    it('copes with a country it has no entry for', () => {
      renderForm({ values: { ...VALUES, countryCode: 'ZZ', state: '' } });

      expect(stateSelect()).toBeInTheDocument();
    });

    // The field has to show what the record holds even when narrowing would hide it,
    // or a viewer sees no state for a user who has one and an editor re-saves a value
    // they were never shown. users.country_code is nullable and most rows are blank,
    // so this is the common case rather than the exotic one.
    //
    // Read from the trigger's text: a KeyDS Select renders its label as the text of a
    // button, so toHaveValue is empty whatever the field shows.
    it('still shows a state stored against no country', () => {
      renderForm({ values: { ...VALUES, countryCode: '', state: 'TX' } });

      expect(stateSelect()).toHaveTextContent('Texas');
    });

    it('still shows a state that does not belong to the chosen country', () => {
      renderForm({ values: { ...VALUES, countryCode: 'CA', state: 'TX' } });

      expect(stateSelect()).toHaveTextContent('Texas');
    });
  });

  // PD-4684. The Authentication field moved onto this page from the legacy form.
  describe('the Authentication field', () => {
    const field = () => screen.getByLabelText('Authentication');

    it('is offered on the page', () => {
      renderForm();

      expect(field()).toBeInTheDocument();
    });

    it('shows what the user signs in with today', () => {
      renderForm({ values: { ...VALUES, ssoProvider: 'paccar' } });

      expect(screen.getByText('PCAR (Pass)')).toBeInTheDocument();
    });

    it('reports a change to the provider', () => {
      const onChange = jest.fn();
      renderForm({ onChange });

      fireEvent.click(field());
      fireEvent.click(screen.getByText('PCAR (Pass)'));

      expect(onChange).toHaveBeenCalledWith('ssoProvider', 'paccar');
    });

    // Rendered with a provider on file so that "Password" appears only in the open
    // list: the field displays its own value, so a password user would match twice.
    it('offers password login', () => {
      const onChange = jest.fn();
      renderForm({ onChange, values: { ...VALUES, ssoProvider: 'paccar' } });

      fireEvent.click(field());
      fireEvent.click(screen.getByText('Password'));

      expect(onChange).toHaveBeenCalledWith('ssoProvider', 'password');
    });

    // KeyDS offers a clear button whenever a value is selected, and this field
    // always has one. Clearing emitted an empty string, which enabled Save on a
    // change the request then dropped, and which the server would read as password
    // login if it were forwarded.
    it('cannot be cleared, being required', () => {
      renderForm({ values: { ...VALUES, ssoProvider: 'paccar' } });

      // KeyDS names it "Clear <label> value", so this cannot be a bare /clear/ —
      // the other selects on this form have clear buttons of their own.
      expect(
        screen.queryByRole('button', { name: /clear authentication value/i }),
      ).not.toBeInTheDocument();
    });

    it("carries the wizard's help line, so both screens describe it alike", () => {
      renderForm();

      expect(
        screen.getByText('This will determine how the user accesses the app.'),
      ).toBeInTheDocument();
    });

    // A domain rule cannot be overridden. A KeyDS Select puts no HTML `disabled`
    // attribute on its button — InputField guards the click handler — so the
    // behaviour is what is checkable.
    describe('when an email domain rule decides', () => {
      const locked = {
        authentication: {
          ...AUTHENTICATION,
          assignment: 'domain',
          locked: true,
          provider: 'decisiv',
          options: [{ value: 'decisiv', label: 'Decisiv Okta' }],
        },
        values: { ...VALUES, ssoProvider: 'decisiv' },
      };

      it('will not open', () => {
        const onChange = jest.fn();
        renderForm({ ...locked, onChange });

        fireEvent.click(field());

        // The field renders its own value, so one copy of the label is expected;
        // a second would be the open list's option.
        expect(screen.queryAllByText('Decisiv Okta')).toHaveLength(1);
        expect(onChange).not.toHaveBeenCalled();
      });

      it('says which provider the domain requires, and that it cannot change', () => {
        renderForm(locked);

        expect(
          screen.getByText(
            'Decisiv Okta is required for this email domain and cannot be changed.',
          ),
        ).toBeInTheDocument();
      });
    });

    it('will not open for a viewer without permission to edit', () => {
      const onChange = jest.fn();
      renderForm({ disabled: true, onChange });

      fireEvent.click(field());

      // One copy is the field showing its value; a second would be the open list.
      expect(screen.queryAllByText('Password')).toHaveLength(1);
      expect(onChange).not.toHaveBeenCalled();
    });
  });
});
